import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Store, 
  ShoppingCart, 
  MapPin, 
  Package, 
  Star,
  Clock,
  ChevronRight,
  Eye,
  RefreshCw,
  Truck,
  CheckCircle,
  XCircle,
  Calendar,
  IndianRupee
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { LocationSelector } from '../components/LocationSelector';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Star Rating Display
const StarRating = ({ rating, size = 16 }) => {
  return (
    <div className="flex items-center gap-1">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          size={size}
          className={i < Math.floor(rating) ? "fill-amber-400 text-amber-400" : "fill-gray-200 text-gray-200"}
        />
      ))}
      <span className="text-sm text-gray-600 ml-1">({rating?.toFixed(1) || '0.0'})</span>
    </div>
  );
};

const CustomerDashboard = () => {
  const { user, getAuthHeader } = useAuth();
  const { t } = useLanguage();
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [orders, setOrders] = useState([]);
  const [location, setLocation] = useState(user?.location || null);
  const [showCart, setShowCart] = useState(false);
  const [showOrders, setShowOrders] = useState(false);
  const [showShopProducts, setShowShopProducts] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('shops'); // 'shops' or 'orders'

  useEffect(() => {
    fetchShops();
    fetchOrders();
  }, []);

  const fetchShops = async () => {
    try {
      const response = await axios.get(`${API}/shops`);
      setShops(response.data);
    } catch (error) {
      toast.error(t('loadFailed'));
    }
  };

  const fetchProducts = async (shopId) => {
    try {
      const response = await axios.get(`${API}/products?shop_id=${shopId}`);
      setProducts(response.data);
      setSelectedShop(shops.find(s => s.id === shopId));
      setShowShopProducts(true);
    } catch (error) {
      toast.error(t('loadFailed'));
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, { headers: getAuthHeader() });
      setOrders(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error('Failed to load orders:', error);
    }
  };

  const addToCart = (product) => {
    const existing = cart.find(item => item.product_id === product.id);
    if (existing) {
      setCart(cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        price: product.price
      }]);
    }
    toast.success(`${product.name} ${t('addToCart')}`);
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const placeOrder = async () => {
    if (!location) {
      toast.error(t('pleaseSetLocation'));
      return;
    }
    if (cart.length === 0) {
      toast.error(t('cartIsEmpty'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/orders`, {
        shop_id: selectedShop.id,
        items: cart,
        delivery_address: location.address,
        delivery_location: { lat: location.lat, lng: location.lng }
      }, { headers: getAuthHeader() });
      
      toast.success(t('orderSuccess'));
      setCart([]);
      setShowCart(false);
      setShowShopProducts(false);
      fetchOrders();
    } catch (error) {
      toast.error(t('orderFailed'));
    } finally {
      setLoading(false);
    }
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  const statusConfig = {
    pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: t('pending') },
    assigned: { color: 'bg-blue-100 text-blue-800', icon: Truck, label: t('assigned') },
    picked_up: { color: 'bg-purple-100 text-purple-800', icon: Package, label: t('pickedUp') },
    delivered: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: t('delivered') },
    cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle, label: t('cancelled') }
  };

  const getOrderStats = () => {
    const total = orders.length;
    const delivered = orders.filter(o => o.status === 'delivered').length;
    const pending = orders.filter(o => ['pending', 'assigned', 'picked_up'].includes(o.status)).length;
    const totalSpent = orders.filter(o => o.status === 'delivered').reduce((sum, o) => sum + o.total_amount, 0);
    return { total, delivered, pending, totalSpent };
  };

  const stats = getOrderStats();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-bold text-3xl md:text-4xl text-gray-900 mb-2">
            {t('welcome')}, {user?.name}!
          </h1>
          <p className="text-gray-600">{t('browseShops')}</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Package className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-sm text-gray-600">{t('totalOrders')}</span>
            </div>
            <p className="font-bold text-2xl text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-gray-600">{t('delivered')}</span>
            </div>
            <p className="font-bold text-2xl text-gray-900">{stats.delivered}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <span className="text-sm text-gray-600">{t('pending')}</span>
            </div>
            <p className="font-bold text-2xl text-gray-900">{stats.pending}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-sm text-gray-600">{t('totalSpent')}</span>
            </div>
            <p className="font-bold text-2xl text-gray-900">₹{stats.totalSpent}</p>
          </div>
        </div>

        {/* Delivery Location */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">{t('deliveryAddress')}</h3>
          <LocationSelector
            defaultLocation={location}
            onLocationSelect={setLocation}
          />
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('shops')}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
              activeTab === 'shops' 
                ? 'bg-emerald-500 text-white shadow-lg' 
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
            data-testid="tab-shops"
          >
            <Store className="w-5 h-5 inline mr-2" />
            {t('shops')}
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
              activeTab === 'orders' 
                ? 'bg-emerald-500 text-white shadow-lg' 
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
            data-testid="tab-orders"
          >
            <Package className="w-5 h-5 inline mr-2" />
            {t('myOrders')} ({orders.length})
          </button>
          <button
            onClick={() => setShowCart(true)}
            className="px-6 py-3 rounded-full font-semibold bg-orange-500 text-white shadow-lg hover:bg-orange-600 transition-all ml-auto"
            data-testid="view-cart-btn"
          >
            <ShoppingCart className="w-5 h-5 inline mr-2" />
            {t('cart')} ({cart.length})
          </button>
        </div>

        {/* Shops Tab */}
        {activeTab === 'shops' && (
          <div>
            <h2 className="font-bold text-2xl text-gray-900 mb-6">{t('shops')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {shops.map((shop) => (
                <div
                  key={shop.id}
                  data-testid={`shop-card-${shop.id}`}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group cursor-pointer"
                  onClick={() => fetchProducts(shop.id)}
                >
                  <div className="h-32 bg-gradient-to-br from-emerald-100 to-orange-100 flex items-center justify-center overflow-hidden">
                    {shop.image_url ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${shop.image_url}`}
                        alt={shop.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Store className="w-16 h-16 text-emerald-500/50" />
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-bold text-lg text-gray-900 group-hover:text-emerald-600 transition-colors">
                          {shop.name}
                        </h3>
                        <p className="text-sm text-gray-500 line-clamp-2 mt-1">{shop.description}</p>
                      </div>
                      <ChevronRight className="text-gray-400 group-hover:text-emerald-500 transition-colors" />
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-3">
                      <MapPin className="w-4 h-4" />
                      <span className="truncate">{shop.address}</span>
                    </div>
                    <div className="mt-3">
                      <StarRating rating={shop.rating} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            <h2 className="font-bold text-2xl text-gray-900 mb-6">{t('orderHistory')}</h2>
            {orders.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('noData')}</h3>
                <p className="text-gray-500 mb-6">{t('startShoppingNow')}</p>
                <Button 
                  onClick={() => setActiveTab('shops')}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full"
                >
                  {t('startShopping')}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => {
                  const status = statusConfig[order.status] || statusConfig.pending;
                  const StatusIcon = status.icon;
                  return (
                    <div 
                      key={order.id} 
                      data-testid={`order-${order.id}`} 
                      className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${status.color}`}>
                              <StatusIcon size={14} />
                              {status.label}
                            </span>
                            <span className="text-sm text-gray-500">
                              #{order.id.slice(0, 8)}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {new Date(order.created_at).toLocaleDateString('en-IN', { 
                                day: 'numeric', 
                                month: 'short', 
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            <span className="flex items-center gap-1">
                              <Package size={14} />
                              {order.items.length} {t('items')}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-2">
                            {order.items.map(i => i.product_name).join(', ')}
                          </p>
                          <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                            <MapPin size={14} />
                            {order.delivery_address}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-bold text-2xl text-gray-900">₹{order.total_amount}</p>
                            <p className="text-xs text-gray-500">{t('cashOnDelivery')}</p>
                          </div>
                          <Button
                            onClick={() => setSelectedOrder(order)}
                            variant="outline"
                            className="rounded-full"
                            data-testid={`view-order-${order.id}`}
                          >
                            <Eye size={16} className="mr-1" />
                            {t('details')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Shop Products Dialog */}
        <Dialog open={showShopProducts} onOpenChange={setShowShopProducts}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{selectedShop?.name}</DialogTitle>
            </DialogHeader>
            <p className="text-gray-500 mb-4">{selectedShop?.description}</p>
            {products.length === 0 ? (
              <div className="text-center py-8">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">{t('noProductsAvailable')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {products.map((product) => (
                  <div
                    key={product.id}
                    data-testid={`product-card-${product.id}`}
                    className="bg-gray-50 rounded-xl p-4 space-y-3"
                  >
                    <div className="aspect-square bg-white rounded-lg overflow-hidden flex items-center justify-center">
                      {product.image_url ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${product.image_url}`} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <Package className="w-12 h-12 text-gray-300" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{product.name}</h4>
                      <p className="text-sm text-gray-500 line-clamp-2">{product.description}</p>
                      <p className="font-bold text-lg text-emerald-600 mt-2">₹{product.price}</p>
                      <p className="text-xs text-gray-400">{t('stock')}: {product.stock}</p>
                    </div>
                    <Button
                      data-testid={`add-to-cart-${product.id}`}
                      onClick={() => addToCart(product)}
                      disabled={product.stock === 0}
                      className="w-full bg-emerald-500 text-white hover:bg-emerald-600 rounded-full"
                    >
                      {product.stock === 0 ? t('outOfStock') : t('addToCart')}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Cart Dialog */}
        <Dialog open={showCart} onOpenChange={setShowCart}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{t('cart')}</DialogTitle>
            </DialogHeader>
            {cart.length === 0 ? (
              <div className="text-center py-8">
                <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">{t('cartEmpty')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cart.map((item) => (
                  <div key={item.product_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{item.product_name}</h4>
                      <p className="text-sm text-gray-500">₹{item.price} {t('each')}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 bg-white rounded-full border border-gray-200">
                        <button 
                          onClick={() => updateQuantity(item.product_id, -1)}
                          className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-900"
                        >
                          -
                        </button>
                        <span className="w-8 text-center font-semibold">{item.quantity}</span>
                        <button 
                          onClick={() => updateQuantity(item.product_id, 1)}
                          className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-900"
                        >
                          +
                        </button>
                      </div>
                      <span className="font-bold text-emerald-600 w-20 text-right">₹{item.price * item.quantity}</span>
                    </div>
                  </div>
                ))}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-bold text-lg">{t('orderTotal')}:</span>
                    <span className="font-bold text-2xl text-emerald-600">₹{cartTotal}</span>
                  </div>
                  <Button
                    data-testid="checkout-btn"
                    onClick={placeOrder}
                    disabled={loading || !location}
                    className="w-full bg-emerald-500 text-white hover:bg-emerald-600 h-12 rounded-full font-semibold"
                  >
                    {loading ? t('loading') : `${t('placeOrder')} (${t('cashOnDelivery')})`}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Order Details Dialog */}
        <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{t('orderDetails')}</DialogTitle>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('orderId')}:</span>
                  <span className="font-mono text-sm">#{selectedOrder.id.slice(0, 8)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('status')}:</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig[selectedOrder.status]?.color}`}>
                    {statusConfig[selectedOrder.status]?.label}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('date')}:</span>
                  <span>{new Date(selectedOrder.created_at).toLocaleString('en-IN')}</span>
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">{t('orderItems')}</h4>
                  {selectedOrder.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between py-2 border-b border-gray-100 last:border-0">
                      <span>{item.product_name} x {item.quantity}</span>
                      <span className="font-semibold">₹{item.price * item.quantity}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="font-bold text-lg">{t('total')}:</span>
                  <span className="font-bold text-2xl text-emerald-600">₹{selectedOrder.total_amount}</span>
                </div>
                <div className="pt-4 border-t">
                  <h4 className="font-semibold mb-2">{t('deliveryAddress')}</h4>
                  <p className="text-gray-600 flex items-start gap-2">
                    <MapPin size={16} className="mt-1 flex-shrink-0" />
                    {selectedOrder.delivery_address}
                  </p>
                </div>
                {selectedOrder.status === 'delivered' && (
                  <Link to={`/shop/${selectedOrder.shop_id}`}>
                    <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full">
                      <Star size={16} className="mr-2" />
                      {t('writeReview')}
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CustomerDashboard;
